/**
 * CHART COLOUR — one module, for every chart on the Dashboard.
 *
 * This is `dashboard-reports.chart-colors.ts` folded in whole, plus the fold
 * helper that `analytics-composition.tsx` had a second, narrower copy of. Two
 * implementations of one design-system rule is how the rule comes to be obeyed
 * in one place and broken in the other, so there is one.
 *
 * WHY IT IS A SEPARATE MODULE FROM THE COMPONENTS
 * -----------------------------------------------
 * So `chart-palette.test.ts` can exercise the rules below without importing a
 * screen — which would drag in recharts, rsuite, jspdf, html2canvas and xlsx.
 * The guard has to be cheap to run or it will be the first thing skipped.
 *
 * The other reason is that a text-scrape of a 2,000-line component is not a
 * guard, it is a tripwire. Every rule here is a FUNCTION or a CONSTANT, so the
 * test can assert the actual colours the screen will draw and recompute their
 * separation with `src/style-utils/dataviz-color-math.ts`, rather than grepping
 * for a pattern and hoping.
 *
 * NOTHING IN THIS FILE IS A HEX LITERAL. Every value resolves to a token in
 * `style-utils/theme.ts`, whose own palette is measured and guarded by
 * `dataviz-palette.test.ts` (OKLab lightness, OKLab chroma, dichromat ΔE, WCAG
 * contrast, all recomputed in-repo).
 */
import { dataViz, palette } from '../../../../../style-utils/theme';

/** A slice, once a colour has been decided for it. */
export interface PieChartData {
  name: string;
  value: number;
  color: string;
  /* Slice outline, only where the FILL is deliberately too faint to define the
     wedge on its own (the parcel pie's "Unrecorded" bucket). Everything else
     takes the white `palette.surface` hairline every slice has always had. */
  edge?: string;
}

/* A taxonomy row before any colour has been assigned to it. Colour is decided
   by `foldToCap` from the row's RANK, so the literals in a screen must not
   carry one: a colour written next to a category name is a promise that
   survives reorders, and that promise is exactly what the cap cannot keep. */
export interface CountedRow {
  name: string;
  value: number;
}

/**
 * ═══════════════ THE FOUR-SLOT CAP, ENFORCED WHERE SLICES ARE BUILT ═══════════
 *
 * `dataViz.series` is validated on ADJACENT pairs — slot n against slot n+1 —
 * which is the right measurement for a line chart, where a reader compares
 * neighbours. A PIE compares every slice against every other, and under
 * ALL-PAIRS validation that order holds to FOUR slots and no more. Measured
 * in-repo with `dataviz-color-math.ts`: the five-slot sets the reports screen
 * used to draw come back at ΔE 5.5 deutan (orange↔green) and 12.1–12.3 normal
 * (magenta↔violet, teal↔green), against floors of 6.0 and 15.0. Not "a bit
 * tight" — two of five slices were the same colour to a good share of readers,
 * inside a chart whose legend swore they were not.
 *
 * So the tail FOLDS. Two ordering rules, and they do different jobs:
 *
 *  · WHO SURVIVES is decided by value, so the bucket always holds the smallest
 *    slices instead of whichever category happens to be declared last.
 *  · WHAT COLOUR a survivor gets is decided by DECLARED order, so the largest
 *    categories keep the same hue from one report to the next. Painting by rank
 *    would repaint Guest teal in March and blue in April, and a hue that moves
 *    is not an identity either.
 *
 * A FOLD OF ONE IS NAMED, NOT CALLED "OTHER". "Other" holding exactly one
 * category is that category with its name deleted for no gain. The slice keeps
 * its name and loses only its hue, which is the honest statement of what
 * happened: this identity is outside the four the palette can encode, so the
 * label carries it instead of the fill. A fold of several says "Other" and
 * names its members, so the tooltip and the legend chip both stay truthful.
 */
export const CATEGORICAL_CAP = 4;

/**
 * The residual bucket's fill: `palette.inkSoft`, chroma 0.030 — far under the
 * 0.10 floor at which a value reads as a hue at all — and 10.36:1 against
 * `palette.surface`.
 *
 * The obvious mid-greys were measured first and do not survive. `slateLight`
 * lands ΔE 4.1 protan / 11.5 normal from brand teal: the "everything else"
 * wedge would read as slice #1 to roughly 8% of men, which is the exact bug
 * this rule exists to remove. `line` and `lineStrong` separate cleanly but are
 * 1.25:1 and 1.48:1 washes on white — a slice that is not there. `slate` fails
 * both gates (13.0 normal from teal, 5.1 deutan from magenta).
 *
 * inkSoft also sits BELOW the 0.43–0.77 categorical lightness band, at L 0.369,
 * and that is deliberate rather than tolerated: the residual bucket must not
 * read as one of the categories, and being outside the band is what stops it.
 */
export const RESIDUAL_FILL = palette.inkSoft;

/**
 * "Unrecorded" — nobody wrote the carrier down. The ONE mark on this screen
 * whose fill sits under the 3:1 contrast gate on purpose: `palette.lineStrong`
 * is a 1.48:1 wash, so a blank field looks blank. What defines the wedge is the
 * hairline instead, `palette.slate` at 4.95:1 on white.
 *
 * It is this way because the palette holds exactly ONE neutral that clears every
 * ΔE gate against the categorical hues (inkSoft, spent on the fold above), and a
 * second dark grey beside it would measure ΔE 10.5 — under the 15 floor. Given
 * the choice between two greys that cannot be told apart and one grey that is
 * quiet, quiet wins: this slice is read by its outline, its label and its
 * tooltip, and never by its fill.
 */
export const UNRECORDED_FILL = palette.lineStrong;
export const UNRECORDED_EDGE = palette.slate;

/** Consecutive slots, which is the validated order. Worst all-pairs: ΔE 16.8
 *  normal (teal↔blue), 6.6 deutan (teal↔magenta) — legal at four slots because
 *  every chart using them carries a legend or direct labels. */
export const CATEGORICAL_SLOTS = [0, 1, 2, 3] as const;

/**
 * ═══════════ ONE NAMED EXCEPTION: EMERGENCY IS A STATE, NOT A CATEGORY ═══════
 *
 * `palette.alert` is a semantic token, and the design system is explicit that
 * semantic tokens are never series colours: the moment coral means "series 5"
 * it stops meaning "incident". This is the one place in the app that breaks that
 * rule on purpose, and it is worth stating why.
 *
 * "Emergency (911)" is a case where a CATEGORY and a STATE coincide, and the
 * state has to win. Assigning it by slot index is how it ended up leaf-green
 * (#4C9F38, which is also `palette.success`) after the palette realignment — a
 * 911 call rendering in the colour the product uses for "checked in, all fine".
 * A reader's safety inference is worth more than palette purity, so Emergency is
 * pinned to `palette.alert` BY NAME and taken out of the categorical rotation
 * entirely, where no future reorder can move it.
 *
 * It never rests on colour alone, which is the other half of the semantic-token
 * rule: the slice ships with its legend chip ("Emergency (911)" and the count,
 * beside the coral dot) and with the tooltip's name and value.
 *
 * THE PINNED SLICE COSTS A HUE, THOUGH — and this is the part that had to be
 * measured rather than assumed. Coral is not free of the categorical scale: it
 * sits ΔE 6.7 normal / 3.0 deutan from slot 1 (orange), so a chart that pins
 * coral CANNOT also draw orange, or an emergency and a prospect become the same
 * wedge. An exhaustive all-pairs search over every 4-subset of `dataViz.series`
 * finds NO four-slot set that survives alongside coral; exactly one three-slot
 * set does — 0/2/3, teal/blue/magenta, at ΔE 16.8 normal and 6.6 deutan. So a
 * chart with a pinned status slice gets three categorical slots, not four, and
 * folds one deeper. `chart-palette.test.ts` pins both halves of that finding,
 * including orange's failure, so "tidying" 0/2/3 back to 0/1/2/3 fails with the
 * number that explains itself.
 */
export const EMERGENCY_TYPE = 'Emergency (911)';
export const EMERGENCY_FILL = palette.alert;
export const ALERT_SAFE_SLOTS = [0, 2, 3] as const;

/**
 * Turn a taxonomy into at most `CATEGORICAL_CAP` coloured slices plus one folded
 * bucket. `alwaysFold` rows never compete for a slot: they are values that
 * already MEAN "residual" (a carrier the desk recorded as "Other"), and
 * promoting one to a hue would put two differently-coloured slices called
 * "Other" in the same pie.
 */
export const foldToCap = (
  rows: CountedRow[],
  slots: readonly number[] = CATEGORICAL_SLOTS,
  alwaysFold: CountedRow[] = []
): PieChartData[] => {
  // The cap is the rule, not a suggestion a caller can pass its way around.
  const usable = slots.slice(0, CATEGORICAL_CAP);
  const declared = new Map(rows.map((row, index) => [row.name, index]));
  const byDeclaredOrder = (a: CountedRow, b: CountedRow) =>
    (declared.get(a.name) ?? 0) - (declared.get(b.name) ?? 0);

  const byValue = [...rows].sort((a, b) => b.value - a.value);
  const kept = byValue.slice(0, usable.length).sort(byDeclaredOrder);
  const folded = [
    ...byValue.slice(usable.length).sort(byDeclaredOrder),
    ...alwaysFold,
  ];

  const slices: PieChartData[] = kept.map((row, index) => ({
    name: row.name,
    value: row.value,
    color: dataViz.series[usable[index]],
  }));

  if (folded.length === 0) return slices;

  return [
    ...slices,
    {
      name:
        folded.length === 1
          ? folded[0].name
          : `Other (${folded.map(row => row.name).join(', ')})`,
      value: folded.reduce((sum, row) => sum + row.value, 0),
      color: RESIDUAL_FILL,
    },
  ];
};

/**
 * THE SAME FOLD, CARRYING THE MEMBERS RATHER THAN NAMING THEM IN THE LABEL.
 *
 * The composition donut needs the folded members as a LIST — its key prints
 * "Other (3 more)" and its tooltip prints "Folded in: a, b, c" — where the
 * reports pies need them inside the slice name, because their legend chip is the
 * only place a name can go. Same ranking, same slots, same cap; one of them
 * stringifies and the other does not.
 */
export interface Slice extends PieChartData {
  key: string;
  label: string;
  tone: string;
  /** The categories folded into this wedge. Empty for a real category. */
  folded: string[];
  /* THE COMPARISON'S VALUE FOR THE SAME SLICE, folded the SAME way.
     It has to be folded here rather than beside the caller, or the two sides
     fold differently the moment their rank orders differ — "Other" on one side
     would then hold a different set of kinds from "Other" on the other, under
     one label, and the two columns would not be comparable at all. */
  compareValue?: number | null;
}

export const OTHER_KEY = '__other__';

/** Neutral, because "Other" is a bucket and not an identity. Exported so every
 *  panel that folds — the composition donut, the move-out reasons, the carrier
 *  pie — says "this is a bucket" in the same colour, rather than each choosing
 *  its own neutral. */
export const OTHER_TONE = RESIDUAL_FILL;

export const foldWithMembers = (
  items: { key: string; label: string; value: number; compareValue?: number | null }[],
  slots: readonly number[] = CATEGORICAL_SLOTS
): Slice[] => {
  const usable = slots.slice(0, CATEGORICAL_CAP);
  const ranked = [...items].sort((a, b) => b.value - a.value);

  const head: Slice[] = ranked.slice(0, usable.length).map((item, i) => ({
    key: item.key,
    label: item.label,
    name: item.label,
    value: item.value,
    color: dataViz.series[usable[i]],
    tone: dataViz.series[usable[i]],
    folded: [],
    compareValue: item.compareValue ?? null,
  }));

  const tail = ranked.slice(usable.length);
  if (!tail.length) return head;

  return [
    ...head,
    {
      key: OTHER_KEY,
      label: 'Other',
      name: 'Other',
      value: tail.reduce((sum, item) => sum + item.value, 0),
      color: OTHER_TONE,
      tone: OTHER_TONE,
      folded: tail.map(item => `${item.label} (${item.value.toLocaleString()})`),
      /* Null, not zero, when NOTHING in the tail had a comparison value: a fold
         of nothing-to-compare must not render as a comparison of zero. */
      compareValue: tail.some(item => item.compareValue != null)
        ? tail.reduce((sum, item) => sum + (item.compareValue ?? 0), 0)
        : null,
    },
  ];
};

/**
 * SEVERITY IS STATE, NOT IDENTITY, AND THAT DECIDES ITS COLOURS.
 *
 * The three levels are a threshold being crossed, which is exactly what
 * `palette.success` / `palette.warning` / `palette.alert` are reserved for. So
 * they wear the semantic tokens — and every one of them ships with its label
 * beside it TWICE (on the axis and in the key underneath), because colour alone
 * is not a label and never was.
 *
 * THE CONSEQUENCE FOR THE REST OF THE SCREEN: once amber means "follow up" on
 * this page, amber is not available as a series colour anywhere on it. Nothing
 * else reaches for the semantic tokens; every categorical panel takes
 * consecutive `dataViz.series` slots, and the delta chips are deliberately
 * verdict-free for the same reason.
 *
 * An unknown level falls back to the bucket neutral, so a fourth level renders
 * as a real bar rather than as a crash.
 */
export const SEVERITY_TONE: Record<string, string> = {
  ok: palette.success,
  follow_up: palette.warning,
  incident: palette.alert,
};

export const severityTone = (key: string): string =>
  SEVERITY_TONE[key] || palette.lineStrong;

/**
 * THE UNIT OCCUPANCY CLASSES. `unrecorded` gets NO fill of its own — it is drawn
 * as a hatch, because it is the ABSENCE of an answer and not a third kind of
 * tenancy. Counting it as vacant is exactly how an occupancy rate becomes
 * fiction, which is why this screen prints no occupancy percentage at all.
 */
export const OCCUPANCY_TONE: Record<string, string> = {
  occupied: dataViz.series[0],
  vacant: palette.lineStrong,
};

export const occupancyTone = (key: string): string =>
  OCCUPANCY_TONE[key] || OTHER_TONE;

/**
 * VALUE -> SEQUENTIAL RAMP STEP, scaled by the server's own `max`.
 *
 * Zero always takes step 0 — the palest — and every non-zero value takes at
 * least step 1, so "one thing happened here" is never painted the same as
 * "nothing happened here". That is the whole distinction the rhythm panel exists
 * to show, and a linear index would collapse it: with a max of 40, a cell of 1
 * rounds to step 0.
 *
 * The ramp is a CONTINUOUS FILL and never a set of swatches. Its first step is
 * ΔL 0.051 from the second against a 0.06 floor for ordered marks, and its
 * palest step is 1.10:1 against `palette.surface` — so a row of chips built from
 * it would show two steps nobody can separate and one nobody can see.
 * `chart-sizing.test.ts` asserts that no panel turns it into swatches.
 */
export const heatTone = (
  value: number,
  max: number,
  ramp: readonly string[] = dataViz.sequential
): string => {
  if (!(value > 0) || !(max > 0)) return ramp[0];
  const steps = ramp.length - 1;
  const index = Math.max(1, Math.min(steps, Math.ceil((value / max) * steps)));

  return ramp[index];
};

/**
 * THE CATEGORICAL SCALE, BY MODE.
 *
 * `light` is what everything reads today. `dark` returns `dataViz.seriesDark`,
 * which was validated in the SAME pass as the light set (dark band L 0.48–0.67,
 * worst adjacent ΔE 10.6 deutan / 21.1 normal, all six over 3:1 on a #1a1a19
 * ground, all-pairs holding to the same four slots) and which NOTHING reads yet,
 * because this application has no dark mode: there is not one
 * `prefers-color-scheme` query and not one `data-theme` attribute anywhere in
 * `src/`.
 *
 * It is a function rather than a constant so that the day dark mode arrives, it
 * arrives through one call site instead of a transplant — deriving a dark scale
 * later and separately is how a light-mode teal ends up in a dark chart at the
 * wrong lightness and quietly falls out of the band.
 */
export type ChartMode = 'light' | 'dark';

export const series = (mode: ChartMode = 'light'): readonly string[] =>
  mode === 'dark' ? dataViz.seriesDark : dataViz.series;

/** Slot n of the categorical scale, wrapping refused.
 *
 *  THE MODULO IS GONE AND IT IS NOT COMING BACK. The reports carrier pie read
 *  `dataViz.series[i % dataViz.series.length]` over whatever carriers the month
 *  happened to contain; the intake list is seven carriers plus an 'Unrecorded'
 *  bucket, so the eighth entry wrapped round to brand teal and rendered
 *  PIXEL-IDENTICAL to the first inside a labelled pie — and the defect got
 *  quietly worse every time the desk added a carrier. A cycled hue is not an
 *  identity, it is a scale that has run out and carried on regardless. Past the
 *  end of the scale this returns the residual neutral, which is the honest
 *  answer: this category is outside what the palette can encode. */
export const slot = (index: number, mode: ChartMode = 'light'): string => {
  const scale = series(mode);

  return index >= 0 && index < scale.length ? scale[index] : RESIDUAL_FILL;
};
